import { useEffect, useState } from 'react';
import { Box, CircularProgress, Paper, Stack, Typography } from '@mui/material';
import {
  Button,
  DeleteWithConfirmButton,
  Exporter,
  ExportButton,
  FunctionField,
  ListControllerResult,
  TextField,
  TopToolbar,
  WrapperField,
  useListContext,
  useNotify,
  useRecordContext,
  downloadCSV,
} from 'react-admin';
import jsonExport from 'jsonexport/dist';
import { Add as AddIcon, Delete as DeleteIcon } from '@mui/icons-material';

import CraftPageSection from 'src/components/CraftPageSection';
import CraftStandaloneList from 'src/components/CraftStandaloneList';
import IntegerInput from 'src/inputs/IntegerInput';
import { usePermissions } from 'src/hooks';
import { CompanyRecord, CompanySubscriptionRecord, CompanySubscriptionSetRecord } from 'src/types';
import { Center } from 'src/components/Center';
import { useCompanySubscriptionSetContext } from '../../Context';
import { useAddCompanySubscriptionsDialogContext } from './AddCompanySubscriptionsDialog';
import { useDeleteCompanySubscriptionsDialogContext } from './DeleteCompanySubscriptionsDialog';

interface ListContextConsumerProps {
  setContext: React.Dispatch<React.SetStateAction<ListControllerResult | undefined>>;
}

const ListContextConsumer = ({ setContext }: ListContextConsumerProps) => {
  const listCtx = useListContext();

  useEffect(() => {
    setContext(listCtx);
  }, []);

  return null;
};

const filters = [<IntegerInput key="id" label="Company ID" source="company_id" alwaysOn />];

type CompaniesById = Record<string | number, CompanyRecord | undefined>;

export const CompanySubscriptionsList = () => {
  const companySubscriptionSetRecord = useRecordContext<CompanySubscriptionSetRecord>();
  const { currentSubscriptionCounts, currentSubscriptionCountsLoading, currentSubscriptionCountsRefetch } =
    useCompanySubscriptionSetContext();
  const addCompanySubscriptionsDialogCtx = useAddCompanySubscriptionsDialogContext();
  const deleteCompanySubscriptionsDialogCtx = useDeleteCompanySubscriptionsDialogContext();
  const { permissions } = usePermissions();
  const notify = useNotify();

  const exporter: Exporter<CompanySubscriptionRecord> = async (records, fetchRelatedRecords) => {
    if (!companySubscriptionSetRecord) return;

    try {
      const relatedCompanies = await fetchRelatedRecords(records, 'company_id', 'companies');
      const companiesById = relatedCompanies as CompaniesById;
      const data = records.map((r) => {
        const company = companiesById[r.company_id];

        return {
          'Company ID': r.company_id,
          'Company Name': company?.name ?? '',
        };
      });

      jsonExport(
        data,
        {
          headers: ['Company ID', 'Company Name'],
        },
        (err: Error | null, csv: string) => {
          if (err) {
            console.error('Failed to export company subscriptions:', err);
            notify('Failed to export company subscriptions', { type: 'error' });
            return;
          }
          downloadCSV(
            csv,
            `subscription_set_companies_${companySubscriptionSetRecord.name.replace(/[^a-z0-9]/gi, '_')}`,
          );
        },
      );
    } catch {
      notify('Failed to export company subscriptions', { type: 'error' });
    }
  };

  const [listCtx, setListCtx] = useState<ListControllerResult>();

  useEffect(() => {
    addCompanySubscriptionsDialogCtx.setOnSuccess(() => {
      currentSubscriptionCountsRefetch();
      listCtx?.refetch();
    });
    deleteCompanySubscriptionsDialogCtx.setOnSuccess(() => {
      currentSubscriptionCountsRefetch();
      listCtx?.refetch();
    });
  }, [currentSubscriptionCountsRefetch, listCtx]);

  if (!companySubscriptionSetRecord || companySubscriptionSetRecord.all_companies) {
    // "All Companies" sets don't have explicit company subscriptions, so this component shouldn't
    // be rendered
    return null;
  }

  const availableSubscriptionSlotCount =
    !currentSubscriptionCountsLoading && currentSubscriptionCounts
      ? companySubscriptionSetRecord.company_limit - currentSubscriptionCounts.total
      : undefined;

  const Actions = (
    <TopToolbar>
      <ExportButton />
      {permissions.has('companySubscription:edit') && (
        <Button
          label="Add"
          variant="outlined"
          startIcon={<AddIcon />}
          onClick={addCompanySubscriptionsDialogCtx.setOpen}
          disabled={currentSubscriptionCountsLoading || (availableSubscriptionSlotCount ?? 0) < 1}
        />
      )}
      {permissions.has('companySubscription:delete') && (
        <Button
          label="Bulk Delete"
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={deleteCompanySubscriptionsDialogCtx.setOpen}
        />
      )}
    </TopToolbar>
  );

  const onDeleteSuccess = () => {
    currentSubscriptionCountsRefetch();
  };

  return (
    <CraftPageSection title="Company Subscriptions">
      <Stack component={Paper} px={1} direction="row" alignItems="center" gap={3}>
        {currentSubscriptionCountsLoading || !currentSubscriptionCounts ? (
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              width: '100%',
              py: '4px',
            }}
          >
            <CircularProgress size={16} />
          </Box>
        ) : (
          <>
            <Typography component="span">Resolved: {currentSubscriptionCounts.resolved}</Typography>
            <Typography component="span">Processing: {currentSubscriptionCounts.reserved}</Typography>
            <Typography component="span" color="primary" fontWeight="700">
              Total: {currentSubscriptionCounts.total}
            </Typography>
          </>
        )}
      </Stack>

      <Typography mt={3} mb={1}>
        Resolved Subscriptions
      </Typography>
      <Paper sx={{ width: '100%', p: 2 }}>
        <CraftStandaloneList
          resource="company_subscriptions"
          filter={{ company_subscription_set_id: companySubscriptionSetRecord.id }}
          filters={filters}
          actions={Actions}
          exporter={exporter}
          dataGridProps={{
            empty: <Center m={4}>No company subscriptions found</Center>,
            bulkActionButtons: false, // TODO: enable bulk delete when BulkDeleteWithConfirmButton is fixed
          }}
        >
          <TextField label="Company ID" source="company_id" />
          <WrapperField label="Actions">
            <FunctionField
              render={(rec: CompanySubscriptionRecord) => (
                <DeleteWithConfirmButton
                  confirmTitle={`Delete Subscription for Company ID ${rec.company_id}?`}
                  redirect={false}
                  disabled={!permissions.has('companySubscription:delete')}
                  mutationOptions={{ onSuccess: onDeleteSuccess }}
                />
              )}
            />
          </WrapperField>

          <ListContextConsumer setContext={setListCtx} />
        </CraftStandaloneList>
      </Paper>
    </CraftPageSection>
  );
};

export default CompanySubscriptionsList;
